import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Box
} from '@mui/material';
import { seasonClubDoc, seasonPlayerDoc, seasonPlayerTransfersCollection } from '../services/players';
import { getDocs, query, orderBy, where, getDoc } from 'firebase/firestore';

function TransfersModal({ open, onClose, currentSeasonId }) {
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch transfers and resolve team names
    useEffect(() => {
        if (open) {
            fetchTransfers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, currentSeasonId]);

    const fetchTransfers = async () => {
        if (!currentSeasonId) {
            setTransfers([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Fetch transfers from Firestore - last 100 days
            const transfersRef = seasonPlayerTransfersCollection(currentSeasonId);
            const hundredDaysAgo = new Date();
            hundredDaysAgo.setDate(hundredDaysAgo.getDate() - 100);
            const cutoffDate = hundredDaysAgo.toISOString().split('T')[0]; // Format: yyyy-mm-dd
            const q = query(transfersRef, where('date', '>=', cutoffDate), orderBy('date', 'desc'));
            const querySnapshot = await getDocs(q);

            const transfersData = [];
            const teamIds = new Set();
            const playerIds = new Set();

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                transfersData.push(data);
                if (data.from_team_id) teamIds.add(String(data.from_team_id));
                if (data.to_team_id) teamIds.add(String(data.to_team_id));
                if (data.player_id) playerIds.add(String(data.player_id));
            });

            const resolvedTransfers = await Promise.all(transfersData.map(async (transfer) => {
                // Resolve Player Name
                // ...existing logic for player name name...
                let playerName = "Laden...";
                if (transfer.player_id && currentSeasonId) {
                     const playerDocRef = seasonPlayerDoc(currentSeasonId, transfer.player_id);
                     try {
                         const playerSnap = await getDoc(playerDocRef);
                         if (playerSnap.exists()) {
                             const pData = playerSnap.data();
                             playerName = pData.common_name || pData.name || pData.lastname || "Onbekend";
                         } else {
                             playerName = `ID: ${transfer.player_id}`; 
                         }
                     } catch (e) {
                          playerName = `ID: ${transfer.player_id}`;
                     }
                }

                // Resolve Team Names
                // Clubs staan onder het seizoen. Staat de club daar, dan doet hij
                // mee; staat hij er niet, dan is het per definitie een club buiten
                // dit seizoen. Voorheen moest hier nog een seasons-array nagelopen
                // worden om datzelfde te bepalen.
                const getTeamName = async (teamId) => {
                    if (!teamId) return "-";

                    try {
                        const clubSnap = await getDoc(seasonClubDoc(currentSeasonId, teamId));
                        return clubSnap.exists() ? clubSnap.data().name : "Non-CL";
                    } catch (err) {
                        console.error("Error fetching club:", err);
                        return "Non-CL";
                    }
                };

                const fromTeam = await getTeamName(transfer.from_team_id);
                const toTeam = await getTeamName(transfer.to_team_id);

                return {
                    ...transfer,
                    playerName,
                    fromTeam,
                    toTeam
                };
            }));

            setTransfers(resolvedTransfers);

        } catch (error) {
            console.error("Error fetching transfers:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Laatste transfers</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a2e' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}>Datum</TableCell>
                                    <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}>Speler</TableCell>
                                    <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}>Van</TableCell>
                                    <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.2)' }}>Naar</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transfers.map((transfer) => {
                                    // Format date to Dutch format dd-mm-yyyy
                                    const formatDate = (dateStr) => {
                                        if (!dateStr) return '-';
                                        const parts = dateStr.split('-');
                                        if (parts.length === 3) {
                                            // Assuming input is yyyy-mm-dd
                                            return `${parts[2]}-${parts[1]}-${parts[0]}`;
                                        }
                                        return dateStr;
                                    };
                                    
                                    const getTeamStyle = (teamName) => ({
                                        color: teamName === 'Non-CL' ? 'rgba(255,255,255,0.4)' : '#fff',
                                        borderColor: 'rgba(255,255,255,0.1)'
                                    });
                                    
                                    return (
                                        <TableRow key={transfer.id}>
                                            <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>{formatDate(transfer.date)}</TableCell>
                                            <TableCell sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.1)' }}>{transfer.playerName}</TableCell>
                                            <TableCell sx={getTeamStyle(transfer.fromTeam)}>{transfer.fromTeam}</TableCell>
                                            <TableCell sx={getTeamStyle(transfer.toTeam)}>{transfer.toTeam}</TableCell>
                                        </TableRow>
                                    );
                                })}
                                {transfers.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ color: '#fff' }}>Geen transfers gevonden</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Sluiten</Button>
            </DialogActions>
        </Dialog>
    );
}

export default TransfersModal;
